import { FRONTEND_PERMISSIONS } from '../permissions.constants';

export interface BusinessPageEntry {
  slug: string;
  name: string;
  icon: string;
  category: BusinessCategory;
  /** Permissions required — user must have ALL of them. */
  requiresPermissions: string[];
  /** Zone types where this page is visible. If omitted, visible in all zone types. */
  onlyInZone?: string[];
  helpId?: string;
  /** Override the router link prefix. Default is /business/. */
  routerLinkPrefix?: string[];
}

export type BusinessCategory =
  | 'organization'
  | 'sales'
  | 'operations'
  | 'finance'
  | 'integrations'
  | 'admin';

export interface BusinessCategoryMeta {
  id: BusinessCategory;
  label: string;
  icon: string;
}

export const BUSINESS_CATEGORIES: BusinessCategoryMeta[] = [
  { id: 'organization', label: 'Organization', icon: 'pi pi-sitemap' },
  { id: 'sales', label: 'Sales & Marketing', icon: 'pi pi-wallet' },
  { id: 'operations', label: 'Operations', icon: 'pi pi-box' },
  { id: 'finance', label: 'Finance', icon: 'pi pi-dollar' },
  { id: 'integrations', label: 'Integrations', icon: 'pi pi-plug' },
  { id: 'admin', label: 'Admin & Settings', icon: 'pi pi-cog' },
];

export const BUSINESS_PAGES: BusinessPageEntry[] = [
  // ── Organization ──────────────────────────────────────────────
  {
    slug: 'general',
    name: 'General',
    icon: 'pi pi-table',
    category: 'organization',
    requiresPermissions: [FRONTEND_PERMISSIONS.franchise],
    helpId: 'business',
  },
  {
    slug: 'units',
    name: 'Business Units',
    icon: 'pi pi-sitemap',
    category: 'organization',
    requiresPermissions: [FRONTEND_PERMISSIONS.franchiseManagement],
    helpId: 'business-units',
  },
  {
    slug: 'areas',
    name: 'Areas',
    icon: 'pi pi-map',
    category: 'organization',
    requiresPermissions: ['zones.view'],
    helpId: 'areas',
  },
  {
    slug: 'tags',
    name: 'Tags',
    icon: 'pi pi-tags',
    category: 'organization',
    requiresPermissions: [FRONTEND_PERMISSIONS.settings],
    helpId: 'business',
  },

  // ── Sales & Marketing ─────────────────────────────────────────
  {
    slug: 'marketing',
    name: 'Marketing',
    icon: 'pi pi-wallet',
    category: 'sales',
    requiresPermissions: [FRONTEND_PERMISSIONS.marketing],
  },
  {
    slug: 'discounts',
    name: 'Discounts',
    icon: 'pi pi-percentage',
    category: 'sales',
    requiresPermissions: [FRONTEND_PERMISSIONS.marketing],
    helpId: 'discounts',
  },
  {
    slug: 'products',
    name: 'Products',
    icon: 'pi pi-tag',
    category: 'sales',
    requiresPermissions: [FRONTEND_PERMISSIONS.products],
    helpId: 'products',
  },
  {
    slug: 'templates',
    name: 'Templates',
    icon: 'pi pi-file-edit',
    category: 'sales',
    requiresPermissions: [FRONTEND_PERMISSIONS.settings],
  },

  // ── Operations ────────────────────────────────────────────────
  {
    slug: 'availability',
    name: 'Availability',
    icon: 'pi pi-calendar',
    category: 'operations',
    requiresPermissions: [FRONTEND_PERMISSIONS.availability],
    helpId: 'availability-schedule',
  },
  {
    slug: 'inventory',
    name: 'Inventory',
    icon: 'pi pi-box',
    category: 'operations',
    requiresPermissions: [FRONTEND_PERMISSIONS.franchise],
  },
  {
    slug: 'assets',
    name: 'Assets',
    icon: 'pi pi-tablet',
    category: 'operations',
    requiresPermissions: [FRONTEND_PERMISSIONS.assets],
  },
  {
    slug: 'documents',
    name: 'Documents',
    icon: 'pi pi-file',
    category: 'operations',
    requiresPermissions: [FRONTEND_PERMISSIONS.franchise],
    helpId: 'documents',
  },
  {
    slug: 'scripts',
    name: 'Scripts',
    icon: 'pi pi-code',
    category: 'operations',
    requiresPermissions: ['scripts.view'],
  },
  {
    slug: 'rules',
    name: 'System Rules',
    icon: 'pi pi-book',
    category: 'operations',
    requiresPermissions: ['rules.view'],
    helpId: 'rules',
  },

  // ── Finance ───────────────────────────────────────────────────
  {
    slug: 'invoices',
    name: 'Invoices',
    icon: 'pi pi-file-check',
    category: 'finance',
    requiresPermissions: [FRONTEND_PERMISSIONS.franchise],
  },
  {
    slug: 'taxes',
    name: 'Taxes',
    icon: 'pi pi-dollar',
    category: 'finance',
    requiresPermissions: ['taxes.view'],
  },
  {
    slug: 'payment',
    name: 'Payments',
    icon: 'pi pi-credit-card',
    category: 'finance',
    requiresPermissions: [FRONTEND_PERMISSIONS.settings],
  },
  {
    slug: 'unmatched-payments',
    name: 'Unmatched Payments',
    icon: 'pi pi-question-circle',
    category: 'finance',
    requiresPermissions: [FRONTEND_PERMISSIONS.settings],
  },
  {
    slug: 'accounting',
    name: 'Accounting',
    icon: 'pi pi-calculator',
    category: 'finance',
    requiresPermissions: [FRONTEND_PERMISSIONS.settings],
  },

  // ── Integrations ──────────────────────────────────────────────
  {
    slug: 'nicejobs',
    name: 'NiceJobs',
    icon: 'pi pi-briefcase',
    category: 'integrations',
    requiresPermissions: [FRONTEND_PERMISSIONS.settings],
    routerLinkPrefix: ['/integration', 'nicejobs'],
  },
  {
    slug: 'acton',
    name: 'Act-On',
    icon: 'pi pi-envelope',
    category: 'integrations',
    requiresPermissions: [FRONTEND_PERMISSIONS.settings],
    routerLinkPrefix: ['/integration', 'acton'],
  },
  {
    slug: 'quickbooks',
    name: 'QuickBooks',
    icon: 'pi pi-building-columns',
    category: 'integrations',
    requiresPermissions: [FRONTEND_PERMISSIONS.settings],
    routerLinkPrefix: ['/integration', 'quickbooks'],
  },
  {
    slug: 'yelp',
    name: 'Yelp',
    icon: 'pi pi-star',
    category: 'integrations',
    requiresPermissions: [FRONTEND_PERMISSIONS.settings],
    routerLinkPrefix: ['/integration', 'yelp'],
  },
  {
    slug: 'ringcentral',
    name: 'RingCentral',
    icon: 'pi pi-headphones',
    category: 'integrations',
    requiresPermissions: [FRONTEND_PERMISSIONS.settings],
    routerLinkPrefix: ['/integration', 'ringcentral'],
  },

  // ── Admin & Settings ──────────────────────────────────────────
  {
    slug: 'settings',
    name: 'Config',
    icon: 'pi pi-cog',
    category: 'admin',
    requiresPermissions: [FRONTEND_PERMISSIONS.config],
  },
  {
    slug: 'communications',
    name: 'Communications',
    icon: 'pi pi-phone',
    category: 'admin',
    requiresPermissions: [FRONTEND_PERMISSIONS.settings],
  },
  {
    slug: 'history',
    name: 'History',
    icon: 'pi pi-history',
    category: 'admin',
    requiresPermissions: [FRONTEND_PERMISSIONS.settings],
  },
  {
    slug: 'prophet',
    name: 'Prophet',
    icon: 'pi pi-chart-line',
    category: 'admin',
    requiresPermissions: [FRONTEND_PERMISSIONS.settings],
    helpId: 'business',
  },
];

export function getBusinessPage(slug: string): BusinessPageEntry | undefined {
  return BUSINESS_PAGES.find((p) => p.slug === slug);
}

export function getBusinessPagePermissions(page: BusinessPageEntry): string[] {
  return [...page.requiresPermissions];
}

export function isBusinessPageVisibleInZone(page: BusinessPageEntry, zoneType: string): boolean {
  if (page.onlyInZone?.length && !page.onlyInZone.includes(zoneType)) { return false; }
  return true;
}

export function isBusinessPageVisible(
  page: BusinessPageEntry,
  zoneType: string,
  hasPermission: (perm: string) => boolean,
): boolean {
  if (!isBusinessPageVisibleInZone(page, zoneType)) { return false; }
  const perms = getBusinessPagePermissions(page);
  if (perms.length && !perms.every((p) => hasPermission(p))) { return false; }
  return true;
}